import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search, Pencil, Trash2, ChevronLeft, ChevronRight, Upload, Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  mlfAdminService, dlfAdminService,
  type MlfRow, type DlfRow, type ListParams, type ListResponse, type MutateResponse, type BulkUploadResponse, type BulkMode,
} from '../../services/lossFactorAdminService';

const PAGE_SIZE = 25;
const STATES = ['QLD', 'NSW', 'ACT', 'VIC', 'SA', 'TAS', 'WA', 'NT'];

type FieldType = 'text' | 'number' | 'select';
type Field<Row> = {
  key: keyof Row & string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  required?: boolean;
};
type Column<Row> = { key: keyof Row & string; label: string; mono?: boolean };

type CrudService<Row> = {
  list: (p: ListParams) => Promise<ListResponse<Row>>;
  create: (b: Partial<Row>) => Promise<MutateResponse<Row>>;
  update: (id: string, b: Partial<Row>) => Promise<MutateResponse<Row>>;
  remove: (id: string) => Promise<{ success: boolean; error?: string }>;
  bulkUpload?: (file: File, mode: BulkMode) => Promise<BulkUploadResponse>;
  exportCsv?: () => Promise<{ success: boolean; error?: string }>;
};

// ── Generic table manager ─────────────────────────────────────
function LossFactorTable<Row extends { id: string }>({
  service, fields, columns, entityLabel, sampleCsv,
}: {
  service: CrudService<Row>;
  fields: Field<Row>[];
  columns: Column<Row>[];
  entityLabel: string;
  sampleCsv?: string;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  // Loss factors are reissued in full each 1 July, so replacing is the normal
  // case; appending a new year onto an old one leaves both in the table.
  const [bulkMode, setBulkMode] = useState<BulkMode>('replace');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResponse | null>(null);
  const bulkFileRef = useRef<HTMLInputElement>(null);
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Debounce search; reset to page 1 when the term changes.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    service.list({ search, page, limit: PAGE_SIZE }).then((res) => {
      if (!active) return;
      if (!res.success) { setError(res.error ?? 'Failed to load'); setRows([]); setCount(0); }
      else { setRows(res.data ?? []); setCount(res.count ?? 0); }
      setLoading(false);
    });
    return () => { active = false; };
  }, [service, search, page]);

  const refresh = () => {
    setLoading(true);
    service.list({ search, page, limit: PAGE_SIZE }).then((res) => {
      if (res.success) { setRows(res.data ?? []); setCount(res.count ?? 0); }
      setLoading(false);
    });
  };

  const openCreate = () => {
    setEditing(null);
    setFormValues({});
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: Row) => {
    setEditing(row);
    const vals: Record<string, string> = {};
    for (const f of fields) {
      const v = (row as Record<string, unknown>)[f.key];
      vals[f.key] = v === null || v === undefined ? '' : String(v);
    }
    setFormValues(vals);
    setFormError(null);
    setDialogOpen(true);
  };

  const buildBody = (): Partial<Row> | null => {
    const body: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = (formValues[f.key] ?? '').trim();
      if (f.required && raw === '') { setFormError(`${f.label} is required`); return null; }
      if (raw === '') { body[f.key] = null; continue; }
      if (f.type === 'number') {
        const n = Number(raw);
        if (!Number.isFinite(n)) { setFormError(`${f.label} must be a number`); return null; }
        body[f.key] = n;
      } else {
        body[f.key] = raw;
      }
    }
    return body as Partial<Row>;
  };

  const handleSave = async () => {
    const body = buildBody();
    if (!body) return;
    setSaving(true);
    setFormError(null);
    const res = editing
      ? await service.update(editing.id, body)
      : await service.create(body);
    setSaving(false);
    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    refresh();
  };

  const handleDelete = async (row: Row) => {
    const label = (row as Record<string, unknown>)[columns[1]?.key ?? columns[0].key];
    if (!window.confirm(`Delete this ${entityLabel} row${label ? ` (${String(label)})` : ''}? This cannot be undone.`)) return;
    const res = await service.remove(row.id);
    if (!res.success) { setError(res.error ?? 'Delete failed'); return; }
    // If we deleted the last row on a page, step back a page.
    if (rows.length === 1 && page > 1) setPage((p) => p - 1);
    else refresh();
  };

  const downloadSample = () => {
    if (!sampleCsv) return;
    const blob = new Blob([sampleCsv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityLabel.toLowerCase()}_sample.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (!service.exportCsv) return;
    setExporting(true);
    const res = await service.exportCsv();
    setExporting(false);
    if (!res.success) setError(res.error ?? 'Export failed');
  };

  const openBulkDialog = () => { setBulkFile(null); setBulkResult(null); setBulkMode('replace'); setBulkDialogOpen(true); };

  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBulkFile(e.target.files?.[0] ?? null);
    setBulkResult(null);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || !service.bulkUpload) return;
    setBulkUploading(true);
    const result = await service.bulkUpload(bulkFile, bulkMode);
    setBulkResult(result);
    setBulkUploading(false);
    if (result.success && (result.inserted ?? 0) > 0) refresh();
  };

  const cell = (row: Row, key: keyof Row & string): string => {
    const v = (row as Record<string, unknown>)[key];
    return v === null || v === undefined || v === '' ? '—' : String(v);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${entityLabel}…`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>
        <span className="text-sm text-muted-foreground">{count.toLocaleString()} rows</span>
        <div className="flex-1" />
        {service.exportCsv && (
          <Button variant="outline" onClick={handleExport} disabled={exporting} className="gap-1.5">
            <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        )}
        {service.bulkUpload && (
          <Button variant="outline" onClick={openBulkDialog} className="gap-1.5">
            <Upload className="h-4 w-4" /> Bulk upload
          </Button>
        )}
        <Button onClick={openCreate} className="gap-1.5">
          <Plus className="h-4 w-4" /> Add {entityLabel}
        </Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center text-muted-foreground py-8">No rows found</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.id}>
                  {columns.map((c) => (
                    <TableCell key={c.key} className={c.mono ? 'font-mono' : undefined}>{cell(row, c.key)}</TableCell>
                  ))}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(row)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row)} aria-label="Delete"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk upload dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!bulkUploading) setBulkDialogOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Upload {entityLabel}</DialogTitle>
            <DialogDescription>
              Upload a CSV or XLSX file. Column headers must match the {entityLabel} field names exactly (case-insensitive).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {sampleCsv && (
              <button
                type="button"
                onClick={downloadSample}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Download className="h-4 w-4" /> Download sample CSV
              </button>
            )}

            <div className="rounded-md border border-input p-3 space-y-2">
              <p className="text-sm font-medium">How should this file be applied?</p>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio" name="bulk-mode" value="replace" className="mt-1"
                  checked={bulkMode === 'replace'}
                  onChange={() => setBulkMode('replace')}
                />
                <span>
                  <span className="font-medium">Replace</span> — clear every existing {entityLabel} row, then load this file.
                  <span className="block text-xs text-muted-foreground">
                    For the annual AEMO reissue. The file becomes the whole table.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="radio" name="bulk-mode" value="append" className="mt-1"
                  checked={bulkMode === 'append'}
                  onChange={() => setBulkMode('append')}
                />
                <span>
                  <span className="font-medium">Append</span> — add these rows, keep what is already there.
                  <span className="block text-xs text-muted-foreground">
                    For a few corrections. Loading a whole year this way leaves two financial years in the table at once.
                  </span>
                </span>
              </label>
              {bulkMode === 'replace' && count > 0 && (
                <Alert variant="destructive">
                  <AlertDescription className="text-xs">
                    Deletes all {count.toLocaleString()} existing {entityLabel} rows before loading. This cannot be undone — export first if you need a copy.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            <div
              className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input p-6 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => bulkFileRef.current?.click()}
            >
              <Upload className="h-8 w-8 text-muted-foreground" />
              {bulkFile ? (
                <span className="text-sm font-medium">{bulkFile.name}</span>
              ) : (
                <span className="text-sm text-muted-foreground">Click to select CSV or XLSX file</span>
              )}
              <input
                ref={bulkFileRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={handleBulkFileChange}
              />
            </div>

            {bulkResult && (
              <div className="space-y-2">
                {bulkResult.success ? (
                  <Alert>
                    <AlertDescription className="space-y-1">
                      <div className="font-medium">Upload complete</div>
                      <div className="text-sm">
                        {bulkResult.mode === 'replace' && `${(bulkResult.deleted ?? 0).toLocaleString()} removed · `}
                        {bulkResult.inserted} inserted · {bulkResult.skipped} skipped · {bulkResult.errors?.length ?? 0} errors (of {bulkResult.total} rows)
                      </div>
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert variant="destructive">
                    <AlertDescription>{bulkResult.error}</AlertDescription>
                  </Alert>
                )}
                {(bulkResult.errors?.length ?? 0) > 0 && (
                  <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs space-y-1">
                    {bulkResult.errors!.map((e) => (
                      <div key={e.row} className="text-red-600">Row {e.row}: {e.error}</div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)} disabled={bulkUploading}>
              {bulkResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!bulkResult?.success && (
              <Button onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}>
                {bulkUploading ? 'Uploading…' : 'Upload'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${entityLabel} row` : `Add ${entityLabel} row`}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the fields and save.' : `Create a new ${entityLabel} record.`}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={`fld-${f.key}`}>
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </Label>
                {f.type === 'select' ? (
                  <select
                    id={`fld-${f.key}`}
                    value={formValues[f.key] ?? ''}
                    onChange={(e) => setFormValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {(f.options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <Input
                    id={`fld-${f.key}`}
                    type={f.type === 'number' ? 'number' : 'text'}
                    step={f.type === 'number' ? '0.00001' : undefined}
                    value={formValues[f.key] ?? ''}
                    onChange={(e) => setFormValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    className={f.type === 'number' ? 'font-mono' : undefined}
                  />
                )}
              </div>
            ))}
          </div>

          {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Field / column configs ────────────────────────────────────
const DIRECTION_OPTIONS = [
  { value: '', label: '— none —' },
  { value: 'import', label: 'import' },
  { value: 'export', label: 'export' },
];
const FACTOR_TYPE_OPTIONS = [
  { value: 'load', label: 'load' },
  { value: 'gen', label: 'gen' },
];
const STATE_OPTIONS = STATES.map((s) => ({ value: s, label: s }));

const MLF_FIELDS: Field<MlfRow>[] = [
  { key: 'region', label: 'Region', type: 'select', options: STATE_OPTIONS, required: true },
  { key: 'factor_type', label: 'Factor type', type: 'select', options: FACTOR_TYPE_OPTIONS, required: true },
  { key: 'direction', label: 'Direction', type: 'select', options: DIRECTION_OPTIONS },
  { key: 'tni_code', label: 'TNI code', type: 'text' },
  { key: 'duid', label: 'DUID', type: 'text' },
  { key: 'location', label: 'Location / Generator', type: 'text' },
  { key: 'voltage_kv', label: 'Voltage (kV)', type: 'text' },
  { key: 'connection_point_id', label: 'Connection point ID', type: 'text' },
  { key: 'current_fy', label: 'Current FY', type: 'text', required: true },
  { key: 'current_mlf', label: 'Current MLF', type: 'number', required: true },
  { key: 'prior_fy', label: 'Prior FY', type: 'text' },
  { key: 'prior_mlf', label: 'Prior MLF', type: 'number' },
];
const MLF_COLUMNS: Column<MlfRow>[] = [
  { key: 'region', label: 'Region' },
  { key: 'tni_code', label: 'TNI', mono: true },
  { key: 'duid', label: 'DUID', mono: true },
  { key: 'factor_type', label: 'Type' },
  { key: 'direction', label: 'Dir' },
  { key: 'location', label: 'Location' },
  { key: 'current_fy', label: 'FY' },
  { key: 'current_mlf', label: 'MLF', mono: true },
  { key: 'prior_mlf', label: 'Prior', mono: true },
];

const DLF_FIELDS: Field<DlfRow>[] = [
  { key: 'state', label: 'State', type: 'select', options: STATE_OPTIONS, required: true },
  { key: 'dnsp', label: 'DNSP', type: 'text', required: true },
  { key: 'dlf_code', label: 'DLF code', type: 'text', required: true },
  { key: 'table_name', label: 'Table name', type: 'text' },
  { key: 'network_level', label: 'Network level', type: 'text' },
  { key: 'nmi', label: 'NMI', type: 'text' },
  { key: 'current_fy', label: 'Current FY', type: 'text', required: true },
  { key: 'current_dlf', label: 'Current DLF', type: 'number', required: true },
  { key: 'prior_fy', label: 'Prior FY', type: 'text' },
  { key: 'prior_dlf', label: 'Prior DLF', type: 'number' },
];
const DLF_COLUMNS: Column<DlfRow>[] = [
  // State first, mirroring Region on the MLF table. Without it a DLF code that
  // repeats across states reads as a duplicate row.
  { key: 'state', label: 'State' },
  { key: 'dnsp', label: 'DNSP' },
  { key: 'dlf_code', label: 'DLF code', mono: true },
  { key: 'network_level', label: 'Network level' },
  { key: 'nmi', label: 'NMI', mono: true },
  { key: 'current_fy', label: 'FY' },
  { key: 'current_dlf', label: 'DLF', mono: true },
  { key: 'prior_dlf', label: 'Prior', mono: true },
];

const MLF_SAMPLE_CSV = [
  'region,factor_type,direction,tni_code,duid,location,voltage_kv,connection_point_id,current_fy,current_mlf,prior_fy,prior_mlf',
  'QLD,load,import,QABM,,Abbot Mountain,,QABM1,2025-26,0.9980,2024-25,0.9970',
  'QLD,gen,export,,ALDGAS1,Alinta Gas,,ALDGAS1,2025-26,1.0010,,',
  'NSW,load,import,NPLM,,Palm Beach,,,2025-26,0.9950,2024-25,0.9960',
].join('\n');

const DLF_SAMPLE_CSV = [
  'state,dnsp,dlf_code,table_name,network_level,nmi,current_fy,current_dlf,prior_fy,prior_dlf',
  'QLD,Energex,ALDP,,ELV,,2025-26,1.0200,2024-25,1.0210',
  'NSW,Ausgrid,NGAZ,,LV,,2025-26,1.0150,2024-25,1.0160',
  'VIC,Jemena,JRED,,LV,,2025-26,1.0180,2024-25,1.0190',
].join('\n');

// ── Top-level component (MLF / DLF tabs) ──────────────────────
export const LossFactorsManagement: React.FC = () => {
  const [kind, setKind] = useState<'mlf' | 'dlf'>('mlf');

  const tabs = useMemo(() => ([
    { id: 'mlf' as const, label: 'Marginal (Transmission)' },
    { id: 'dlf' as const, label: 'Distribution' },
  ]), []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Loss Factors</h1>
        <p className="text-sm text-muted-foreground">
          Manage the transmission (MLF) and distribution (DLF) loss-factor reference data.
          Note: the annual sheet re-import performs a full refresh and will overwrite manual edits.
        </p>
      </div>

      <div className="flex gap-2 border-b border-border">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setKind(t.id)}
            className={
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ' +
              (kind === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground')
            }
          >
            {t.label}
            {kind === t.id && <Badge variant="secondary" className="ml-2 align-middle">active</Badge>}
          </button>
        ))}
      </div>

      {kind === 'mlf' ? (
        <LossFactorTable<MlfRow>
          service={mlfAdminService}
          fields={MLF_FIELDS}
          columns={MLF_COLUMNS}
          entityLabel="MLF"
          sampleCsv={MLF_SAMPLE_CSV}
        />
      ) : (
        <LossFactorTable<DlfRow>
          service={dlfAdminService}
          fields={DLF_FIELDS}
          columns={DLF_COLUMNS}
          entityLabel="DLF"
          sampleCsv={DLF_SAMPLE_CSV}
        />
      )}
    </div>
  );
};

export default LossFactorsManagement;
